// frontend/src/components/InstallPrompt.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import pushSubscriptionService from '../services/pushSubscriptionService';
import { useAuth } from '../contexts/AuthContext';
import { isMobileInstallDevice, isStandalonePwa } from '../utils/pwaDisplayMode';
import { matchesRoutePrefix } from '../utils/routeMatch';
import {
  INSTALL_OUTCOME,
  INSTALL_SURFACE,
  promptInstall,
  useInstallAvailability,
  useInstallBannerHeld,
  useInstallSurfaceTurn,
} from '../utils/pwaInstall';
import {
  Box,
  Button,
  Paper,
  Typography,
  Slide,
  IconButton,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { X } from 'lucide-react';

// DEV-ONLY review affordance. The banner needs a real `beforeinstallprompt`
// event, which only Chrome/Android fires and which no desktop review session
// can produce, so `?preview=install-banner` renders it anywhere. CRA inlines
// `process.env.NODE_ENV` at build time, so in a production bundle this reads
// `"production" === "development"` - a static false the minifier deletes along
// with every caller's branch.
const isDevPreview = () =>
  process.env.NODE_ENV === 'development' &&
  new URLSearchParams(window.location.search).get('preview') === 'install-banner';

// Route gate. This component is mounted app-globally (App.js, inside <Router>
// and <AuthProvider>), so without a gate the banner pins itself over the
// homepage, the blog and the legal pages - the SEO surfaces - for visitors who
// have no account to install an app for.
//
// An ALLOWLIST of signed-in app-area prefixes, deliberately not a denylist of
// public paths: a new marketing page must default to NO banner. The failure
// mode of a missing entry here is a banner that does not appear on one app
// screen; the failure mode of a missing denylist entry is a fixed strip pinned
// over a brand-new landing page. Fail closed.
//
// Excluded on purpose even though they are signed-in routes: `/payment*` (a
// fixed strip over checkout is a conversion risk) and the two accessibility
// test pages (developer surfaces).
const APP_ROUTE_PREFIXES = [
  '/dashboard',
  '/analytics',
  '/personal-area',
  '/profile',
  '/settings',
  '/interventions',
  '/business-intelligence',
  '/website',
  '/recommender-dashboard',
  // Segment-bounded like every entry: matches `/recommender` and
  // `/recommender/...` (the earnings screen), never `/recommender-dashboard`
  // (listed above) or any public page.
  '/recommender',
  '/admin',
];

/**
 * Is this one of the signed-in app-area routes the install banner may sit on?
 * Segment-bounded (utils/routeMatch.js, shared with PwaAwarenessSnackbar), so
 * `/website` (the builder, signed-in) matches while `/business-website` (the
 * public landing page) does not.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
const isAppRoute = (pathname) =>
  APP_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix));

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  // The `beforeinstallprompt` event is NOT owned here any more: it fires once,
  // can be replayed once, and the dashboard's push announcement dialog offers
  // the same install. utils/pwaInstall.js captures it before React mounts and
  // hands both surfaces the same single-use event - see that module's header.
  const canInstall = useInstallAvailability();
  // A surface that owns the screen (the dashboard's push announcement dialog)
  // asks the banner to stand down while it is up - see utils/pwaInstall.js.
  const bannerHeld = useInstallBannerHeld();
  // Both are read from providers this component is already mounted inside
  // (App.js: <Router> -> <AuthProvider> -> <InstallPrompt />), so the gate needs
  // no change at the mount site.
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  // No first-steps wait here: on Android the banner is the real one-tap
  // installer and shows from the sign-up day (owner decision 2026-11-03). The
  // iOS snackbar is the one that waits for the first-steps push step.
  //
  // ONE install ask on screen (owner hard rule): this banner and the bottom
  // snackbar never render together - see utils/pwaInstall.js.
  const surface = useInstallSurfaceTurn(INSTALL_SURFACE.TOP_BANNER);
  const surfaceBlocked = surface.blocked;
  const claimSurface = surface.claim;
  // Frozen per mount (lazy initializer, evaluated once) - this component lives
  // above the router and survives every navigation, so a value re-read from the
  // URL could flip mid-life and let the show-counter increment after all.
  const [devPreview] = useState(isDevPreview);

  // The banner element itself. The swipe gesture drives its transform
  // directly (see onTouchMove) rather than through state, so a drag never
  // re-renders the tree while the finger is down.
  const paperRef = useRef(null);
  // { startY, dy, active } — gesture bookkeeping, deliberately a ref: reading
  // it back inside touchend must see the value the last touchmove wrote, with
  // no dependency on React having flushed a state update in between.
  const dragRef = useRef({ startY: 0, dy: 0, active: false });
  // "The banner has already had its turn this page load." The show-counter must
  // be incremented exactly once even though the availability effect below can
  // re-run (Chrome may fire `beforeinstallprompt` again after a declined
  // prompt), so it is a ref, not state - nothing renders off it.
  const shownRef = useRef(false);
  // Pending "give the node back to MUI" timer from settleBanner (below).
  const settleTimerRef = useRef(null);

  // Configuration
  const STORAGE_KEY = 'pwa-install-show-count';
  const MAX_SHOWS = 2;
  const SHOW_DELAY_MS = 5000;
  // Upward travel (px) that commits the dismissal on release.
  const SWIPE_DISMISS_PX = 48;
  // The banner is pinned to the top edge, so a downward drag has nowhere to
  // go: it is resisted and capped, purely as "I felt that" feedback.
  const MAX_DOWN_DRAG_PX = 12;

  // Text direction. Its own effect: it depends on the document, not on install
  // availability, and must not be re-evaluated every time the store changes.
  useEffect(() => {
    const htmlDir = document.documentElement.dir;
    const htmlLang = document.documentElement.lang;
    setIsRTL(htmlDir === 'rtl' || htmlLang === 'he');
  }, []);

  useEffect(() => {
    // Dev-only preview: show at once, skipping the show-count / already-
    // installed / mobile-width gates AND the availability gate - the counter's
    // only writer is below that gate, so it is never incremented. There is no
    // captured event to replay, which the install handler already treats as a
    // no-op; the X button and swipe-to-dismiss (the affordances actually under
    // review) write nothing and work normally.
    if (devPreview) {
      claimSurface();
      setShowPrompt(true);
      return undefined;
    }

    // Check if banner has already been shown the max number of times
    const checkDismissed = () => {
      const count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      return count >= MAX_SHOWS;
    };

    // A phone or tablet, by DEVICE and width: a narrow desktop or Chromebook
    // Chrome window passes the width test and fires the install event, so
    // width alone put the banner on computers (no install ask there - owner
    // decision 2026-11-03). See `isMobileInstallDevice`.
    const isMobileDevice = () => window.innerWidth <= 768 && isMobileInstallDevice();

    // Don't show for a signed-out visitor or anywhere outside the app area,
    // while another surface holds the banner down or the bottom snackbar holds
    // the install ask, or if it is already dismissed, installed, not mobile,
    // has nothing to install, or already took its turn.
    if (
      !isAuthenticated ||
      !isAppRoute(pathname) ||
      bannerHeld ||
      surfaceBlocked ||
      !canInstall ||
      shownRef.current ||
      checkDismissed() ||
      isStandalonePwa() ||
      !isMobileDevice()
    ) {
      return undefined;
    }

    // Show the custom prompt after delay and increment the show counter
    const timer = setTimeout(() => {
      // The bottom snackbar took the install ask first: stand down, no showing.
      if (!claimSurface()) return;
      shownRef.current = true;
      setShowPrompt(true);
      const count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      localStorage.setItem(STORAGE_KEY, String(count + 1));
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
    // Every gate above is a dependency: the effect must re-run when auth
    // resolves, on navigation, and when the last hold clears - the banner then
    // simply re-arms its own delay instead of racing anyone else's.
  }, [devPreview, canInstall, bannerHeld, surfaceBlocked, claimSurface, isAuthenticated, pathname]);

  // Retire a banner that is already up once it no longer belongs on screen:
  //   * the shared event went away - this banner installed the app, or the
  //     dashboard announcement dialog consumed the same single-use event, and
  //     an Install button with nothing left to replay is a dead button;
  //   * the user navigated out of the app area or signed out, where the banner
  //     must never be visible at all.
  // (A pending timer is cleared by the previous effect's own cleanup.)
  // A HOLD deliberately does not hide an already-visible banner: holds exist to
  // stop it appearing over a dialog, and the z-index below already puts it
  // behind every modal - yanking a strip out from under a finger mid-swipe
  // would be worse than leaving it.
  useEffect(() => {
    if (devPreview) return;
    if (!canInstall || !isAuthenticated || !isAppRoute(pathname)) {
      setShowPrompt(false);
    }
  }, [devPreview, canInstall, isAuthenticated, pathname]);

  // NOTE: there is deliberately NO auto-dismiss timer. The banner stays until
  // the user acts on it (install / X / swipe up). It used to disappear after
  // 7s, which made it effectively invisible - the whole point of removing it
  // is that the dismissal affordances below have to be excellent instead.

  // Handle install button click
  const handleInstallClick = async () => {
    const outcome = await promptInstall();

    // Also the dev-preview path: there is no captured event to replay, so the
    // button is inert and the banner stays put, exactly as before.
    if (outcome === INSTALL_OUTCOME.UNAVAILABLE) {
      return;
    }

    setShowPrompt(false);

    if (outcome === INSTALL_OUTCOME.ACCEPTED) {
      localStorage.setItem(STORAGE_KEY, String(MAX_SHOWS));
      // Auto-subscribe to push notifications after PWA install (fire-and-forget)
      pushSubscriptionService.subscribe().catch(() => {});
    }
  };

  // Handle dismiss button click
  const handleDismissClick = useCallback(() => {
    setShowPrompt(false);
  }, []);

  // ── Swipe-up-to-dismiss ────────────────────────────────────────────────────
  // Previously this read touch positions out of React state and returned early
  // on a falsy value, so a drag that began at clientY === 0 (reachable: the
  // banner sits at top: 0) was silently ignored. More importantly the Paper
  // carried `touch-action: pan-y`, which hands every vertical gesture to the
  // browser's scroller - the page scrolled, the element got a touchcancel, and
  // the swipe simply never dismissed anything. The surface is now
  // `touch-action: none` so the vertical gesture belongs to us.

  const SETTLE_MS = 180;

  const settleBanner = useCallback(() => {
    dragRef.current.active = false;
    const el = paperRef.current;
    if (!el) return;
    el.style.transition = `transform ${SETTLE_MS}ms ease-out, opacity ${SETTLE_MS}ms ease-out`;
    el.style.transform = 'none';
    el.style.opacity = '1';

    // Hand the node back to MUI once the settle has played out. An inline
    // `transition` left on the element outlives the gesture and then competes
    // with Slide's exit (configured at 260ms), so a later dismissal animates on
    // the leftovers of a swipe that did not commit. Cleared on a timer rather
    // than `transitionend`, which never fires when the drag ended at offset 0
    // and there is nothing to animate back.
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      const node = paperRef.current;
      // Not mid-gesture: onTouchStart sets `none` deliberately, and clearing it
      // under a moving finger would restore Slide's 500ms transform transition
      // and bring back the "banner lags the finger" bug.
      if (node && !dragRef.current.active) node.style.transition = '';
    }, SETTLE_MS + 20);
  }, [SETTLE_MS]);

  // A settle in flight when the banner unmounts would fire against a detached
  // node - harmless, but the timer is cheap to cancel and leaks nothing.
  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  const onTouchStart = useCallback((e) => {
    const touch = e.targetTouches[0];
    if (!touch) return;
    // A pending settle-cleanup would otherwise clear the `none` set below.
    clearTimeout(settleTimerRef.current);
    dragRef.current = { startY: touch.clientY, dy: 0, active: true };
    // MUI's Slide leaves a 500ms transform transition on the node once it has
    // finished entering. Without clearing it the banner trails half a second
    // behind the finger and the gesture reads as broken.
    const el = paperRef.current;
    if (el) el.style.transition = 'none';
  }, []);

  const onTouchMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    const touch = e.targetTouches[0];
    if (!touch) return;

    drag.dy = touch.clientY - drag.startY;
    const offset =
      drag.dy < 0 ? drag.dy : Math.min(drag.dy * 0.25, MAX_DOWN_DRAG_PX);
    // Fade as it travels, so "this is leaving" is legible before release.
    const opacity = drag.dy < 0 ? Math.max(1 + drag.dy / 160, 0.35) : 1;

    const el = paperRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = `translateY(${offset}px)`;
    el.style.opacity = String(opacity);
  }, [MAX_DOWN_DRAG_PX]);

  const onTouchEnd = useCallback(() => {
    const drag = dragRef.current;
    if (!drag.active) return;
    if (-drag.dy >= SWIPE_DISMISS_PX) {
      drag.active = false;
      // Slide's exit transition picks the banner up from wherever the finger
      // left it and carries it the rest of the way off-screen.
      handleDismissClick();
      return;
    }
    settleBanner();
  }, [SWIPE_DISMISS_PX, handleDismissClick, settleBanner]);

  return (
    <Slide
      direction="down"
      // Never without the install-ask claim (belt for the timer's claim).
      in={showPrompt && surface.owns}
      // Entering is unhurried; leaving should feel immediate.
      timeout={{ enter: 500, exit: 260 }}
      mountOnEnter
      unmountOnExit
    >
      <Paper
        ref={paperRef}
        elevation={0}
        role="region"
        aria-label={isRTL ? 'התקן את Leadify AI' : 'Install Leadify AI'}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={settleBanner}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // 1150, chosen against MUI's z-index scale (mobileStepper 1000,
          // fab/speedDial 1050, appBar 1100, drawer 1200, modal 1300,
          // snackbar 1400, tooltip 1500). It sits in the gap MUI leaves between
          // appBar and drawer:
          //   ABOVE appBar (1100) - the banner is a fixed top strip and the
          //   navbar is `position="fixed"` at the same edge, so a lower value
          //   would hide it behind the navbar entirely.
          //   BELOW drawer/modal (1200/1300) - which is the whole point of the
          //   change. Every Dialog, Menu, Popover and Select is a MUI Modal at
          //   1300, so with no auto-dismiss the banner can no longer park a
          //   ~72px clickable strip on top of an open dialog. 9999 put it above
          //   every overlay layer in the app.
          zIndex: 1150,
          // Soft, airy turquoise→pink gradient with slight transparency — the
          // same elegant palette as the homepage CTA / onboarding tour, frosted
          // over the page below (replaces the old "screaming" saturated banner).
          background:
            'linear-gradient(135deg, rgba(224, 247, 242, 0.92) 0%, rgba(241, 250, 253, 0.92) 50%, rgba(252, 234, 243, 0.92) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 0,
          borderBottom: '1px solid rgba(64, 224, 208, 0.25)',
          boxShadow: '0 6px 24px rgba(15, 118, 110, 0.10)',
          direction: isRTL ? 'rtl' : 'ltr',
          // `none`, NOT `pan-y`: the vertical gesture on this strip is the
          // dismissal, so the browser must not claim it for page scrolling.
          // Cost is that a page scroll cannot START on the banner - it is a
          // ~72px band at the very top edge, and it buys a swipe-to-dismiss
          // that fires every time. Taps are unaffected by touch-action.
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            gap: 1.5,
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          {/* App Icon */}
          <Box
            component="img"
            src="/android-chrome-192x192.png"
            alt="Leadify AI"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(15, 118, 110, 0.18)',
              border: '1px solid rgba(64, 224, 208, 0.18)',
            }}
          />

          {/* Text Content */}
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: '#334155',
                letterSpacing: '-0.01em',
              }}
            >
              {isRTL ? 'התקן את Leadify AI' : 'Install Leadify AI'}
            </Typography>
          </Box>

          {/* Install Button */}
          <Button
            variant="contained"
            startIcon={isRTL ? null : <GetAppIcon />}
            endIcon={isRTL ? <GetAppIcon /> : null}
            onClick={handleInstallClick}
            sx={{
              flexShrink: 0,
              background: 'rgba(255, 255, 255, 0.85)',
              color: '#0f766e',
              fontWeight: 700,
              fontSize: '12px',
              padding: '5px 14px',
              borderRadius: '9px',
              textTransform: 'none',
              border: '1px solid rgba(13, 148, 136, 0.25)',
              boxShadow: '0 1px 6px rgba(15, 118, 110, 0.12)',
              minHeight: '32px',
              backdropFilter: 'blur(6px)',
              // LOGICAL margins, deliberately - do NOT "simplify" these back to
              // marginLeft/marginRight. This app has no RTL emotion cache and
              // nothing sets `theme.direction`, so `theme.direction` is always
              // 'ltr' even while this banner renders inside dir="rtl". MUI's own
              // startIcon/endIcon defaults are therefore PHYSICAL and sized for
              // LTR (endIcon: marginLeft 8px / marginRight -4px). In RTL the
              // endIcon sits visually to the LEFT of the label, so that -4px
              // lands between icon and text and they collide (owner-reported).
              // marginInline* resolves against the container's `dir`, so the
              // edge facing the label gets the 8px gap in BOTH directions.
              '& .MuiButton-startIcon': {
                marginInlineEnd: '8px',
                marginInlineStart: '-2px',
              },
              '& .MuiButton-endIcon': {
                marginInlineStart: '8px',
                marginInlineEnd: '-2px',
              },
              '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
                fontSize: '16px',
              },
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                border: '1px solid rgba(13, 148, 136, 0.4)',
                boxShadow: '0 3px 10px rgba(15, 118, 110, 0.20)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.25s ease-in-out',
            }}
          >
            {isRTL ? 'התקן' : 'Install'}
          </Button>

          {/* Close (X) Button — out at the reading edge, so it never shifts the
              centered title. Pairs with the smaller Install button above.
              44x44 is the WCAG 2.5.5 / platform minimum tap target: the icon
              stays visually small (18px) while the touchable area is a full
              thumb, because with the auto-dismiss gone this is the primary way
              out of a banner that otherwise stays put. */}
          <IconButton
            onClick={handleDismissClick}
            aria-label={isRTL ? 'סגירה' : 'Close'}
            sx={{
              flexShrink: 0,
              color: '#64748b',
              width: 44,
              height: 44,
              p: 0,
              // Grow the hit area INTO the container's 16px edge padding
              // instead of into the (already tight at 375px) title column:
              // the old small button occupied ~34px of row width, this one
              // occupies ~36px. Logical property, so it reads correctly in
              // both RTL and LTR without a direction branch.
              marginInlineEnd: '-8px',
              '&:hover': { background: 'rgba(15, 118, 110, 0.08)', color: '#0f766e' },
              '&:focus-visible': {
                outline: '2px solid #0f766e',
                outlineOffset: '2px',
              },
            }}
          >
            <X size={18} />
          </IconButton>

        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPrompt;
